import { useState, useRef, useEffect } from "react";
import { Zap, Clock, Package } from "../common/Icons";

const ServicesSection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const services = [
    { icon: <Zap className="text-green-400" />, title: 'Servicio Rápido', desc: 'Pago vía PayPal o Zelle', detail: 'Procesamiento inmediato.', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.25)' },
    { icon: <Clock className="text-yellow-400" />, title: 'Servicio Intermedio', desc: 'Pago en Bolívares o Stablecoins', detail: 'Procesamiento en 2-3 días.', color: '#eab308', bg: 'rgba(234,179,8,0.08)', border: 'rgba(234,179,8,0.25)' },
    { icon: <Package className="text-pink-400" />, title: 'Servicio Lento', desc: 'Pago en efectivo USD', detail: 'Procesamiento en 5-10 días.', color: '#ec4899', bg: 'rgba(236,72,153,0.08)', border: 'rgba(236,72,153,0.25)' },
  ];

  const steps = [
    { icon: '🔍', color: '#06b6d4', title: 'Verificación de Precios', desc: 'Se verifica si hay variaciones. En caso de cambios, se notifica al cliente para decidir sobre ajustes o reembolsos.' },
    { icon: '📦', color: '#f59e0b', title: 'Procesamiento del Pedido', desc: '3 días hábiles para envío + 7-12 días de tránsito según tipo de envío seleccionado.' },
    { icon: '🚚', color: '#22c55e', title: 'Seguimiento', desc: 'Se crea un grupo temporal para seguimiento del envío hasta su llegada al destino final.' },
    { icon: '🏠', color: '#ec4899', title: 'Entrega Final', desc: 'Entrega los sábados en el local. Delivery disponible (coordinación previa). 1-2 días de conciliación en Caracas.' },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg,#050508,#08050f,#050508)' }}
      data-testid="services-section"
    >
      {/* BG separator lines */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right,transparent,rgba(124,58,237,0.4),transparent)' }} />

      <div className="container mx-auto px-6 md:px-12">
        {/* Header / Toggle */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between group mb-6"
          data-testid="services-accordion-btn"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <h2 className="text-2xl md:text-3xl font-black" style={{ fontFamily: 'Cinzel,serif', background: 'linear-gradient(135deg,#fff,#d4af37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Nuestros Servicios
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm" style={{ color: '#d4af37' }}>
            <span className="hidden sm:block font-semibold">{isOpen ? 'Cerrar' : 'Ver detalles'}</span>
            <div className="w-8 h-8 rounded-full flex items-center justify-center transition-all" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </div>
          </div>
        </button>

        {/* Service cards preview (always visible) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {services.map((s, i) => (
            <div
              key={i}
              className="p-4 rounded-2xl flex items-center gap-4 transition-all hover:scale-[1.02]"
              style={{
                background: s.bg,
                border: `1px solid ${s.border}`,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `all 0.5s ease ${i * 0.1}s`,
              }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.bg}`, border: `1px solid ${s.border}` }}>
                {s.icon}
              </div>
              <div>
                <p className="font-bold text-sm" style={{ color: s.color }}>{s.title}</p>
                <p className="text-gray-400 text-xs">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Expandable detail */}
        <div className="overflow-hidden transition-all duration-500" style={{ maxHeight: isOpen ? '1000px' : '0', opacity: isOpen ? 1 : 0 }}>
          <div className="pt-2 pb-6">
            {/* Steps */}
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 hidden md:block" style={{ background: 'linear-gradient(to bottom,transparent,rgba(212,175,55,0.3),transparent)' }} />

              <div className="space-y-4">
                {steps.map((step, i) => (
                  <div key={i} className="flex gap-5 p-4 rounded-2xl transition-all hover:bg-white/[0.02]" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xl" style={{ background: `${step.color}15`, border: `1px solid ${step.color}30` }}>
                      {step.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm mb-1" style={{ color: step.color }}>{step.title}</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Important note */}
            <div className="mt-6 p-4 rounded-2xl flex gap-4" style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)' }}>
              <span className="text-xl flex-shrink-0">⚠️</span>
              <div>
                <p className="font-bold text-sm text-red-400 mb-1">Nota Importante</p>
                <p className="text-gray-400 text-sm">
                  Envíos aéreos salen los viernes (pedidos recibidos hasta el martes). De no llegar el martes de la semana en curso, saldrá el lunes siguiente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
